import { ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { DashedGhostPreviewRenderer } from '~/engine/view/DashedGhostPreviewRenderer';
import { PlaneView } from '~/engine/view/views/PlaneView';

/**
 * Per-frame data required to draw a single suggestion ghost.
 *
 * `polygonsVoxel` are in the texture grid's voxel-centered
 * continuous coordinate system (the output of marching squares).
 * `targetVoxelZ` is the Z voxel coordinate of the target slice in
 * the same grid. `textureGrid` is used to convert each polygon
 * vertex from voxel space to patient (world) space.
 *
 * `roiColor` is the structure color in [0,1] floats.
 */
export type ContourAutoSuggestionPreviewState = {
    polygonsVoxel: ReadonlyArray<ReadonlyArray<vec2>>;
    targetVoxelZ: number;
    textureGrid: Grid;
    roiColor: ReadonlyVec3;
};

/**
 * The auto-suggestion face of {@link DashedGhostPreviewRenderer}: maps the
 * marching-squares output (voxel space, one colour for the whole state)
 * onto the shared renderer's world-space, per-polygon-colour contract.
 * The conversion happens once per {@link setState} — the grid and target
 * slice are fixed for a given state, so a later {@link refresh} (pan/
 * zoom) re-projects the SAME world points rather than re-deriving them.
 */
export class ContourAutoSuggestionPreviewRenderer {
    private readonly inner: DashedGhostPreviewRenderer;

    public constructor(planeView: PlaneView) {
        this.inner = new DashedGhostPreviewRenderer(planeView);
    }

    public setState(state: ContourAutoSuggestionPreviewState): void {
        this.inner.setState({
            polygons: state.polygonsVoxel.map((poly) => ({
                points: poly.map((v) =>
                    state.textureGrid.toWorldSpace(
                        vec3.fromValues(v[0], v[1], state.targetVoxelZ + 0.5)
                    )
                ),
                color: state.roiColor,
            })),
        });
    }

    /** Re-render with the current state (e.g. after a pan/zoom event). */
    public refresh(): void {
        this.inner.refresh();
    }

    public clear(): void {
        this.inner.clear();
    }

    public dispose(): void {
        this.inner.dispose();
    }
}
